import { get } from 'svelte/store';
import { app, setGlobalStore } from '$lib/stores/app/store';
import { user } from '$lib/stores/user/store';
import { ApiService } from '$lib/services/api';
import * as Sentry from '@sentry/sveltekit';
import {
	AB_API_HOSTS,
	DEVICE_KEY_MAP,
	PROFILE,
	EXPERIMENT_KEYS,
	DATA_TYPES,
	ACTIVE_EXPERIMENTS
} from '$lib/services/experiments/client/constants';
import { ProductExperienceService } from '$lib/services/product-experience';
import { AOFL_PRODUCT_NAMES } from '$lib/utils/constants';
import { browser } from '$app/environment';
import { page } from '$app/stores';
import { isbot } from 'isbot';
import { AnalyticsService } from '$lib/services/analytics';

/**
 * Service for managing A/B testing experiments.
 */
class ExperimentService {
	/**
	 * The API host for the A/B testing service, based on the current environment.
	 * @returns {string} - The API host for the A/B testing service, based on the current environment.
	 */
	get AB_API_HOST() {
		const {
			global: { environment }
		} = get(app);
		return AB_API_HOSTS[environment];
	}

	/**
	 * Constructs the device data array for the API call.
	 * @returns {Array<object>} - An array of device data objects.
	 */
	getDeviceData() {
		const appMeta = get(app).meta;
		const appGlobal = get(app).global;

		const appData = Object.entries(DEVICE_KEY_MAP).flatMap(([key, metaKey]) => {
			if (appMeta[metaKey] === undefined) return [];
			return [
				{
					type: DATA_TYPES.DEVICE,
					id: appMeta.deviceId,
					key,
					value: String(appMeta[metaKey])
				}
			];
		});

		const globalData = Object.entries(DEVICE_KEY_MAP).flatMap(([key, globalKey]) => {
			if (appGlobal[globalKey] === undefined) return [];
			return [
				{
					type: DATA_TYPES.DEVICE,
					id: appMeta.deviceId,
					key,
					value: String(appGlobal[globalKey])
				}
			];
		});

		return [...appData, ...globalData];
	}

	/**
	 * Constructs the user data object for the API call.
	 * @returns {object} - The user data object.
	 */
	getUserData() {
		const appMeta = get(app).meta;
		const userStore = get(user);
		return {
			device_id: appMeta.deviceId,
			master_id: userStore.userToProductUuid || 'null',
			session_id: 'null',
			user_id: userStore.userToProductUuid || 'null',
		};
	}

	/**
	 * Fetches experiments with a given payload, updates the store, and returns the results.
	 * @param {object} payload - The payload for the getExperiments API call.
	 * @example
	 * const payload = {
	 *   user: {
	 *     device_id: 'some-device-id',
	 *     session_id: 'some-session-id',
	 *     user_id: 'some-user-id',
	 *     master_id: 'some-master-id'
	 *   },
	 *   experiment_keys: ['exp_key_1', 'exp_key_2'],
	 *   profile: 'default',
	 *   data: [
	 *     {
	 *       type: 'device',
	 *       id: 'some-device-id',
	 *       key: 'platform',
	 *       value: 'web'
	 *     }
	 *   ]
	 * };
	 * @returns {Promise<void|undefined>} - A promise that resolves when the store is updated.
	 */
	async getExperiments(payload) {
		try {
			const response = await ApiService.getExperiments(this.AB_API_HOST, payload);
			const newExperiments = response?.results;

			if (!newExperiments) {
				setGlobalStore({ experiments: [] });
				return;
			}

			const {
				global: { experiments: oldExperiments }
			} = get(app);
			const seenState = new Map();
			if (oldExperiments) {
				for (const exp of oldExperiments) {
					if (exp.seen) {
						seenState.set(exp.key, true);
					}
				}
			}

			const experimentsForStore = newExperiments.map((exp) => ({
				...exp,
				seen: seenState.has(exp.key) || false
			}));

			setGlobalStore({ experiments: experimentsForStore });
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: Failed To Get Experiments'
				}
			});
		}
	}

	/**
	 * Initializes the experiments for the current user/device on app load.
	 * @returns {Promise<void|undefined>} - A promise that resolves when the experiments have been initialized.
	 */
	async initializeExperiments() {
		const userAgent = navigator?.userAgent ?? '';
		if(isbot(userAgent)) {
			const existingExperiments = get(app).global.experiments;
			if(!existingExperiments || existingExperiments.length == 0) {
				const defaultExperiments = EXPERIMENT_KEYS.map((key) => ({
					key,
					variant: 'control_variant',
					seen: false,
					assigned: false,
					experiment_id: null
				}));
				setGlobalStore({ experiments: defaultExperiments });
				return;
			}

			AnalyticsService.trackClientDebugMetric('experimentBotSessionDetected', {
				user_agent: userAgent,
			});
			return;
		}
		return this.getExperiments({
			user: this.getUserData(),
			experiment_keys: EXPERIMENT_KEYS,
			profile: PROFILE,
			data: this.getDeviceData(),
			auto_mark_seen: false
		});
	}

	/**
	 * Gets the assigned variant for a specific experiment.
	 * @param {string} experimentKey - The key of the experiment to check.
	 * @param {{page_url: string, page_name: string}} pageMetadata - The page metadata to be sent to the API.
	 * @param {boolean} fireEvent - Whether to fire the 'seen' analytic event.
	 * @returns {Promise<string|null>} - The assigned variant for the experiment, or null if the experiment is not found.
	 */
	async getExperimentArm(experimentKey, pageMetadata, fireEvent = true) {
		const {
			global: { experiments },
			meta,
		} = get(app);

		if (!experiments || experiments.length === 0 || !experimentKey) return null;

		const experiment = experiments.find((exp) => exp.key === experimentKey);
		const assignedVariant = experiment?.variant || null;

		if(browser) {
			// @ts-ignore
			pageMetadata.navigator = window.navigator.userAgent;
			// @ts-ignore
			pageMetadata.window_url = window.location.href;
			// @ts-ignore
			pageMetadata.svelte_page_url = get(page).url;
			// @ts-ignore
			pageMetadata.is_tablet = meta.isTabletViewer;
			// @ts-ignore
			pageMetadata.is_desktop = meta.isDesktopViewer;
		}

		if (fireEvent && experiment?.experiment_id && !experiment?.seen) {
			const payload = {
				...experiment,
				profile: PROFILE,
				user: this.getUserData(),
				pageMetadata: pageMetadata
			};

			try {
				await ApiService.markExperimentSeen(this.AB_API_HOST, payload);
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: Failed To Mark Experiment Seen',
						payload
					}
				});
			}

			experiment.seen = true;
			setGlobalStore({ experiments });
		}

		if(assignedVariant?.includes('test')) {
			ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE_2_0);
		}
		return assignedVariant;
	}

	/**
	 * Checks if a specific experiment variant is active and marks the experiment as seen.
	 * @param {string} experimentKey - The key of the experiment to check.
	 * @param {string} argVariant - The client variant used to check against the assigned variant.
	 * @param {{page_url: string, page_name: string}} pageMetadata - The page metadata to be sent to the API.
	 * @param {boolean} fireEvent - Whether to fire the 'seen' analytic event.
	 * @returns {Promise<boolean>} - True if the experiment variant is active, false otherwise.
	 */
	async isExperimentArmActive(experimentKey, argVariant, pageMetadata, fireEvent = true) {
		const assignedVariant = await this.getExperimentArm(experimentKey, pageMetadata, fireEvent);
		return assignedVariant === argVariant;
	}

	/**
	 * Checks if any of the given variants are active for a specific experiment.
	 * @param {string} experimentKey - The key of the experiment to check.
	 * @param {string[]} variants - The variants to check against.
	 * @param {{page_url: string, page_name: string}} pageMetadata - The page metadata to be sent to the API.
	 * @param {boolean} fireEvent - Whether to fire the 'seen' analytic event.
	 * @returns {Promise<boolean>} - True if any of the variants are active, false otherwise.
	 */
	async isAnyExperimentArmActive(experimentKey, variants, pageMetadata, fireEvent = true) {
		const assignedVariant = await this.getExperimentArm(experimentKey, pageMetadata, fireEvent);
		if(!assignedVariant) return false;
		return variants.includes(assignedVariant);
	}

	/**
	 * Checks if at least one of the provided experiment keys is assigned.
	 * @param {string[]} experimentKeys - The experiment keys to check.
	 * @returns {boolean} - True if any of the given experiments are assigned, false otherwise.
	 */
	isAnyExperimentAssigned(experimentKeys) {
		const {
			global: { experiments }
		} = get(app);

		if (!Array.isArray(experimentKeys) || experimentKeys.length === 0) return false;
		if (!experiments || experiments.length === 0) return false;

		// TODO: String check can be removed once experiment service persists assigned value across calls
		for (const key of experimentKeys) {
			const experiment = experiments.find((exp) => exp.key === key);
			if (experiment?.assigned || typeof experiment?.experiment_id === 'string') return true;
		}

		return false;
	}
}

export const experimentService = new ExperimentService();
